import React, { forwardRef, memo, useImperativeHandle, useMemo, useRef } from 'react';
import { Box, useTheme } from '@mui/material';
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso';

import {
  CachedRowCalculator,
  isDataRow,
  ModelChildLoader,
  Row,
  RowDerivationContext,
  RowGroup,
} from '@paragon/layout-models';
import { createScrollbarStyling } from '@paragon/themes';

import { SelectedItemIdentifier } from '../../../../library/selection/types';
import { expandNavigationPanelRowsIfNeeded } from '../../../../store/actions/panelDataActions';
import { getExpandedGridRows } from '../../../../store/selectors/getExpandedGridRows';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { NavigationPanelRow } from './NavigationPanelRow';
import { isComponentDesignLikeRow, NavigationPanelRowValue } from './NavigationPanelRowValue';
import { ExpandCollapseButton } from './row-buttons/ExpandCollapseButton';
import { RowIndentation } from './RowIndentation';
import { ShiftSelectProvider } from './ShiftSelectContext';

export interface GridProps extends RowDerivationContext {
  readonly height: number;
  readonly topLevelRows: RowGroup | RowGroup[];
}

export interface GridMethods {
  scrollToRow(row: RowLike): void;
  focusRow(row: RowLike): void;
}

type RowLike = Row | SelectedItemIdentifier | string;

export const DesignClientGrid = forwardRef<GridMethods, GridProps>((props, ref) => {
  const virtuosoRef = useRef<VirtuosoHandle>(null);

  const theme = useTheme();
  const dispatch = useAppDispatch();

  // note that "visible" here means "visible in the grid [but potentially not scrolled to]", and not necessarily
  //   "actually visible on the screen"
  const visibleRowsRef = useRef<Row[]>([]);

  const expandedRowIds = useAppSelector(getExpandedGridRows);
  const cachedRowCalculator = useMemo(() => new CachedRowCalculator(), []);

  const { topLevelRows, height, ...calculationContext } = props;

  const allRowsWithMetadata = cachedRowCalculator.calculateRows(topLevelRows, calculationContext, expandedRowIds);
  const visibleRowsWithMetadata = allRowsWithMetadata.filter((row) => row.isVisible);

  // for the `scrollToRow` method to find a newly visible row after programmatically expanding parent row(s)
  visibleRowsRef.current = visibleRowsWithMetadata.map(({ row }) => row);

  function resolveRow(rowLike: RowLike): Row | undefined {
    if (rowLike instanceof Row) return rowLike;

    if (typeof rowLike === 'string') return allRowsWithMetadata.find(({ row }) => row.rowId === rowLike)?.row;

    const matchingDataRowsForSelectedItem = allRowsWithMetadata.filter(
      ({ row }) =>
        isDataRow(row) &&
        row.id === rowLike.guid &&
        (rowLike.type === 'componentDesign' ? isComponentDesignLikeRow(row) : row.type === rowLike.type),
    );

    // If there are multiple matching rows, we can't determine which one to scroll to. This is rare enough, and the
    //   consequences of scrolling to the wrong one visually jarring enough, that I've decided to disable auto-scrolling
    //   in this case. - David
    if (matchingDataRowsForSelectedItem.length === 1) {
      return matchingDataRowsForSelectedItem[0].row;
    }
  }

  function scrollRowIntoView(rowLike: RowLike) {
    const row = resolveRow(rowLike);

    if (height <= 0 || row == null) {
      return; // expected and benign; there are multiple grids with auto-scrolling and most rows are only present in one
    }

    const index = visibleRowsRef.current.findIndex((visibleRow) => visibleRow.rowId === row.rowId);

    if (index === -1 || virtuosoRef.current == null) {
      // unlike the cases above, this is worth warning about since we do expect the row to exist in this grid
      console.warn('Failed to scroll to row', row.rowId);
    } else {
      virtuosoRef.current.scrollIntoView({ index, align: 'center' });
    }
  }

  function focusRow(rowLike: RowLike) {
    const row = resolveRow(rowLike);

    if (row == null) {
      return; // expected and benign; there are multiple grids with auto-scrolling and most rows are only present in one
    }

    let rowsNeedExpanding = false;
    if (row.parent) {
      rowsNeedExpanding = dispatch(expandNavigationPanelRowsIfNeeded(row.ancestors.map((row) => row.rowId)));
    }

    if (rowsNeedExpanding) {
      setTimeout(() => {
        // the target row is likely not yet present; wait for the next render when visible rows will be re-calculated
        scrollRowIntoView(row);
      });
    } else {
      scrollRowIntoView(row);
    }
  }

  useImperativeHandle(ref, () => ({ scrollToRow: scrollRowIntoView, focusRow }));

  return (
    <ShiftSelectProvider visibleRowsRef={visibleRowsRef}>
      <Virtuoso
        ref={virtuosoRef}
        increaseViewportBy={200}
        overscan={{ main: height, reverse: height }}
        data={visibleRowsWithMetadata}
        style={{
          height,
          ...createScrollbarStyling(theme),
        }}
        itemContent={(_, { indentationLevel, expandable, row }) => (
          <GridRow level={indentationLevel} row={row} expandable={expandable} loader={props.loaders} />
        )}
      />
    </ShiftSelectProvider>
  );
});

const GridRow = memo((props: { level: number; row: Row; expandable: boolean; loader: ModelChildLoader }) => (
  <NavigationPanelRow key={props.row.rowId} row={props.row}>
    {isComponentDesignLikeRow(props.row) ? (
      <Box sx={{ marginLeft: 2 }} />
    ) : (
      <>
        <RowIndentation level={props.level} />
        <ExpandCollapseButton row={props.row} visible={props.expandable} />
      </>
    )}

    <NavigationPanelRowValue row={props.row} loader={props.loader} />
  </NavigationPanelRow>
));
